import { useForm } from "react-hook-form";
import { Button, Form } from "@telluscompany/design-system";
import { Filter } from "lucide-react";

import { useLocalization } from "@i18n";

import type { AdminStructureReservoirManagementFilters } from "../../api/structures/structures.types";

export type AdminStructureReservoirManagementFilterData = {
  recordedOn: string;
};

const EMPTY_FORM: AdminStructureReservoirManagementFilterData = {
  recordedOn: "",
};

export type AdminStructureReservoirManagementFilterProps = {
  onFilter: (filters: AdminStructureReservoirManagementFilters) => void;
  onClear: () => void;
  initialFilters?: Partial<AdminStructureReservoirManagementFilterData>;
};

export const AdminStructureReservoirManagementFilter = ({
  onFilter,
  onClear,
  initialFilters,
}: AdminStructureReservoirManagementFilterProps) => {
  const { translate } = useLocalization();

  const form = useForm<AdminStructureReservoirManagementFilterData>({
    defaultValues: {
      recordedOn: initialFilters?.recordedOn ?? "",
    },
  });

  const handleFilter = (data: AdminStructureReservoirManagementFilterData) => {
    const filters: AdminStructureReservoirManagementFilters = {};
    if (data.recordedOn.trim()) {
      filters.recordedOn = data.recordedOn.trim();
    }
    onFilter(filters);
  };

  const handleClear = () => {
    form.reset(EMPTY_FORM);
    onClear();
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleFilter)}>
        <div className="flex flex-column gap-5">
          <label className="group flex w-fit max-w-xs flex-col gap-1">
            <span className="text-label">
              {translate(
                "admin.structures.reservoirManagement.filter.label.recordedOn",
              )}
              :
            </span>
            <input
              type="date"
              aria-label={translate(
                "admin.structures.reservoirManagement.filter.label.recordedOn",
              )}
              data-testid="reservoir-management-filter-recorded-on"
              className="h-10 w-44 rounded-xl border border-border bg-background px-4 text-sm shadow-sm"
              {...form.register("recordedOn")}
            />
          </label>
        </div>
        <div className="flex justify-end mb-4 gap-4">
          <Button type="button" variant="secondary" onClick={handleClear}>
            {translate(
              "admin.structures.reservoirManagement.filter.button.clearFilters",
            )}
          </Button>
          <Button
            type="submit"
            data-testid="reservoir-management-filter-submit"
            iconLeft={<Filter fill="currentColor" />}
          >
            {translate(
              "admin.structures.reservoirManagement.table.filterButtonText",
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
};
